import React, { useState, useEffect } from 'react';
import { List, Datagrid, TextField, EmailField, NumberField, TextInput, SelectInput, usePermissions } from 'react-admin';
import { Box, Button } from '@mui/material';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import { checkPermission, exportTimesheet } from '../dataProvider';
import { ImportAccountingDialog } from '../users/ImportAccountingDialog';

/**
 * Known department choices sourced from Azure AD sync.
 * Department values are stored lowercase; labels are title-cased for display.
 * Mirrors the choices used by {@link ../users/UserList}.
 */
const departmentChoices = [
    { id: 'hr', name: 'HR' },
    { id: 'nexus', name: 'Nexus' },
    { id: 'song', name: 'Song' },
    { id: 'dlvry', name: 'Dlvry' },
    { id: 'anesis', name: 'Anesis' },
];

/**
 * Employment-status choices for the status filter.
 *
 * `employees_view.status` is computed server-side as `'active'` or
 * `'terminated'` based on `termination_date`. Omitting the filter (the
 * default "Any" choice) returns both.
 */
const statusChoices = [
    { id: 'active', name: 'Active' },
    { id: 'terminated', name: 'Terminated' },
];

/**
 * Filter inputs rendered above the employee list.
 *
 * Always-on filters:
 * - **Full Name** (`full_name`) — free-text LIKE search
 * - **Email** (`email`) — free-text LIKE search
 * - **Department** (`department`) — select from known Azure AD departments
 * - **Status** (`status`) — select Active, Terminated, or Any (default; no filter sent)
 */
const employeeFilters = [
    <TextInput key="full_name" source="full_name" label="Name" alwaysOn />,
    <TextInput key="email" source="email" label="Email" alwaysOn />,
    <SelectInput key="department" source="department" label="Department" choices={departmentChoices} alwaysOn />,
    <SelectInput key="status" source="status" label="Status" choices={statusChoices} emptyText="Any" alwaysOn />,
];

/**
 * Props for the {@link EmployeeListActions} toolbar component.
 */
interface EmployeeListActionsProps {
    /** Whether the current user holds the `employees.manage` permission. */
    canManage: boolean;
    /** Callback to open the Import Accounting dialog. */
    onImportClick: () => void;
}

/**
 * Action toolbar rendered at the top-right of the employee list.
 *
 * - Renders an "Import Accounting" button for users with the `employees.manage`
 *   permission. Clicking opens the {@link ImportAccountingDialog}.
 * - Renders an "Export Timesheet" button for users with the `employees.export`
 *   permission. Clicking triggers an authenticated download of the multi-sheet
 *   XLSX accounting timesheet for the current year.
 *
 * @param props - See {@link EmployeeListActionsProps}.
 */
const EmployeeListActions: React.FC<EmployeeListActionsProps> = ({ canManage, onImportClick: _onImportClick }) => {
    const { permissions } = usePermissions<string[]>();
    const [exporting, setExporting] = useState(false);

    const canExport = Array.isArray(permissions) && permissions.includes('employees.export');

    /**
     * Triggers the authenticated XLSX timesheet download.
     * Displays an alert on failure to inform the user.
     */
    const handleExport = async (): Promise<void> => {
        setExporting(true);
        try {
            await exportTimesheet();
        } catch (err) {
            alert(`Timesheet export failed: ${(err as Error).message}`);
        } finally {
            setExporting(false);
        }
    };

    if (!canManage && !canExport) return null;

    return (
        <Box sx={{ display: 'flex', gap: 1 }}>
            {/* {canManage && (
                <Button variant="outlined" size="small" onClick={onImportClick}>
                    Import Accounting
                </Button>
            )} */}
            {canExport && (
                <Button
                    variant="outlined"
                    size="small"
                    startIcon={<FileDownloadIcon />}
                    onClick={handleExport}
                    disabled={exporting}
                >
                    {exporting ? 'Exporting…' : 'Export Timesheet'}
                </Button>
            )}
        </Box>
    );
};

/**
 * Displays the list of employee records (employment accounting data) with
 * support for filtering by name and email.
 *
 * Columns shown:
 * - Full Name (from `employees`)
 * - Email
 * - Department
 * - Title (Vietnamese job title)
 * - Contract Type
 * - Join Date
 * - Termination Date
 * - Documents (count of uploaded documents per employee)
 * - Status
 *
 * An "Import Accounting" button is shown to users with the `employees.manage`
 * permission. Clicking it opens the {@link ImportAccountingDialog}.
 *
 * An "Export Timesheet" button is shown to users with the `employees.export`
 * permission. Clicking it triggers an authenticated download of the multi-sheet
 * XLSX accounting timesheet.
 */
export const EmployeeList: React.FC = () => {
    const [canManage, setCanManage] = useState(false);
    const [importOpen, setImportOpen] = useState(false);

    useEffect(() => {
        checkPermission('employees.manage').then(setCanManage);
    }, []);

    return (
        <>
            <List
                filters={employeeFilters}
                actions={
                    <EmployeeListActions
                        canManage={canManage}
                        onImportClick={() => setImportOpen(true)}
                    />
                }
            >
                <Datagrid rowClick="show" bulkActionButtons={false}>
                    <TextField source="full_name" label="Full Name" />
                    <EmailField source="email" label="Email" />
                    <TextField source="department" label="Department" />
                    <TextField source="title" label="Title" />
                    <TextField source="contract_type" label="Contract Type" />
                    <TextField source="join_date" label="Join Date" />
                    <TextField source="termination_date" label="Termination Date" />
                    <NumberField source="document_count" label="Documents" />
                    <TextField source="status" label="Status" />
                </Datagrid>
            </List>
            <ImportAccountingDialog open={importOpen} onClose={() => setImportOpen(false)} />
        </>
    );
};
